import React, { useEffect, useMemo, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import WindMap from '../../../../map-views/WindMap';
import RenewablePotentialChartCard from './RenewablePotentialChartCard';
import RenewableMapLoadingOverlay from './states/RenewableMapLoadingOverlay';
import RenewableMapErrorOverlay from './states/RenewableMapErrorOverlay';
import WindOnshoreOffshoreLegendPanel from './WindOnshoreOffshoreLegendPanel';
import WindPotentialStatisticsRow from './WindPotentialStatisticsRow';

const WindEnergyPotentialCard = ({
  countryIso,
  onshoreWindData,
  offshoreWindData,
  onshoreWindThresholds,
  offshoreWindThresholds,
  isWindLoading,
  windErrors,
  isTabActive = true,
}) => {
  const [showOnshoreLayer, setShowOnshoreLayer] = useState(true);
  const [showOffshoreLayer, setShowOffshoreLayer] = useState(true);

  // Reset toggles when the selected country changes so each country starts
  // from the default "show both layers" behavior.
  useEffect(() => {
    setShowOnshoreLayer(true);
    setShowOffshoreLayer(true);
  }, [countryIso]);

  const mapView = useMemo(() => (
    <WindMap
      countryIso={countryIso}
      onshoreWindData={onshoreWindData}
      offshoreWindData={offshoreWindData}
      onshoreWindThresholds={onshoreWindThresholds}
      offshoreWindThresholds={offshoreWindThresholds}
      showOnshoreLayer={showOnshoreLayer}
      showOffshoreLayer={showOffshoreLayer}
      className="w-full h-full"
      layoutActive={isTabActive}
    />
  ), [
    countryIso,
    onshoreWindData,
    offshoreWindData,
    onshoreWindThresholds,
    offshoreWindThresholds,
    isTabActive,
    showOnshoreLayer,
    showOffshoreLayer
  ]);


  const overlayLegend = useMemo(() => (
    <WindOnshoreOffshoreLegendPanel
      onshoreWindData={onshoreWindData}
      offshoreWindData={offshoreWindData}
      onshoreWindThresholds={onshoreWindThresholds}
      offshoreWindThresholds={offshoreWindThresholds}
      showOnshoreLayer={showOnshoreLayer}
      showOffshoreLayer={showOffshoreLayer}
      placement="overlay"
    />
  ), [
    onshoreWindData,
    offshoreWindData,
    onshoreWindThresholds,
    offshoreWindThresholds,
    showOnshoreLayer,
    showOffshoreLayer
  ]);

  const inlineLegend = useMemo(() => (
    <WindOnshoreOffshoreLegendPanel
      onshoreWindData={onshoreWindData}
      offshoreWindData={offshoreWindData}
      onshoreWindThresholds={onshoreWindThresholds}
      offshoreWindThresholds={offshoreWindThresholds}
      showOnshoreLayer={showOnshoreLayer}
      showOffshoreLayer={showOffshoreLayer}
      placement="inline"
    />
  ), [
    onshoreWindData,
    offshoreWindData,
    onshoreWindThresholds,
    offshoreWindThresholds,
    showOnshoreLayer,
    showOffshoreLayer
  ]);

  const statisticsGrid = useMemo(
    () => <WindPotentialStatisticsRow onshoreWindData={onshoreWindData} offshoreWindData={offshoreWindData} />,
    [onshoreWindData, offshoreWindData]
  );


  const overlayContent = useMemo(() => {

    if (isWindLoading) {
      return (
        <RenewableMapLoadingOverlay
          message="Loading wind data..."
        />
      );
    }

    if (windErrors.onshore || windErrors.offshore) {
      return (
        <RenewableMapErrorOverlay title="Unable to Load Wind Data">

          <div className="text-xs text-red-600 space-y-1">

            {windErrors.onshore && (
              <div>Onshore: {windErrors.onshore}</div>
            )}

            {windErrors.offshore && (
              <div>Offshore: {windErrors.offshore}</div>
            )}

          </div>

        </RenewableMapErrorOverlay>
      );
    }

    return null;

  }, [isWindLoading, windErrors]);

  const headerToggles = useMemo(() => (
    <div className="flex flex-shrink-0 items-center justify-end gap-1.5 sm:gap-2 flex-wrap">
      <button
        type="button"
        onClick={() => setShowOnshoreLayer((v) => !v)}
        className="flex items-center gap-1.5 rounded text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors text-xs font-medium px-1 py-0.5"
        title={showOnshoreLayer ? 'Hide Wind Onshore' : 'Show Wind Onshore'}
        aria-label={showOnshoreLayer ? 'Hide Wind Onshore' : 'Show Wind Onshore'}
      >
        {showOnshoreLayer ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
        <span>Onshore</span>
      </button>
      <button
        type="button"
        onClick={() => setShowOffshoreLayer((v) => !v)}
        className="flex items-center gap-1.5 rounded text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors text-xs font-medium px-1 py-0.5"
        title={showOffshoreLayer ? 'Hide Wind Offshore' : 'Show Wind Offshore'}
        aria-label={showOffshoreLayer ? 'Hide Wind Offshore' : 'Show Wind Offshore'}
      >
        {showOffshoreLayer ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
        <span>Offshore</span>
      </button>
    </div>
  ), [showOnshoreLayer, showOffshoreLayer]);


  return (
    <RenewablePotentialChartCard title="Wind Energy Potential" icon={headerToggles}>
      <div className="h-96 min-h-[16rem] sm:h-96 w-full min-w-0 relative bg-gray-100 overflow-hidden">

        {mapView}

        {overlayContent}

        {overlayLegend}

      </div>

      <div className="w-full min-w-0 mt-3 sm:mt-4 flex flex-wrap items-center gap-2 overflow-x-auto pb-0.5">

        {statisticsGrid}

        <div className="ml-auto mr-1 sm:mr-2 flex-shrink-0">

          {inlineLegend}

        </div>

      </div>

    </RenewablePotentialChartCard>
  );
};

export default WindEnergyPotentialCard;